import {usePlantRunningTimes} from '@domain/running-times'
import {mapRunningTimeDto} from '@domain/shared'
import {usePlantConfig} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {DataContentWrapper} from '@hconnect/common/containers'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import {
  StoppageDto as Stoppage,
  RunningTimeDto,
  RunningTime,
  EquipmentRunningTimesDto as EquipmentRunningTimes,
  EquipmentData,
  RunningTimeStoppage,
  DateRange
} from '@hconnect/common/types'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Theme, useMediaQuery} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {SplitStoppageDialog} from '@widgets/common-running-times'
import {getInitialStoppageData, OperationHoursDrawer} from '@widgets/running-times'
import {StoppageCreate, StoppageDelete} from '@widgets/shift-event-details'
import moment from 'moment-timezone'
import {useCallback, useEffect, useState, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {
  StoppageAnalysisHeader,
  StoppageAnalysisGraph,
  StoppageAnalysisListContainer,
  StoppageAnalysisSideCard
} from '../containers'
import {useStoppageAnalysisDateRange, useStoppageAnalysisPageParams} from '../hooks'
import {StoppageAnalysisLayout} from '../layouts'
import {mapStoppageData} from '../mappers'
import {type StoppageDataDto} from '../types'
import {getFirstStoppageId} from '../utils'

const isSameDateRange = (timeRange: DateRange, zoomedRange: DateRange) =>
  timeRange.startDate.isSame(zoomedRange.startDate) && timeRange.endDate.isSame(zoomedRange.endDate)

type StoppageAnalysisProps = {
  plantId: string
  pageTitle?: string
}

export const StoppageAnalysisPage = ({plantId, pageTitle}: StoppageAnalysisProps) => {
  const timezone = useUpmPlantTimezone(plantId)
  const {timeRangeParam, setTimeRangeParam, equipmentId, replaceEquipmentIdParam} =
    useStoppageAnalysisPageParams()
  if (!equipmentId) {
    throw new Error('equipmentId is missing in the URL parameters')
  }
  const {from, to} = useStoppageAnalysisDateRange({timezone})

  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const queryClient = useQueryClient()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false)
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState<boolean>(false)
  const [stoppageData, setStoppageData] = useState<Partial<Stoppage> | undefined>(undefined)
  const [equipmentIdToPreview, setEquipmentIdToPreview] = useState<string | undefined>(equipmentId)
  const [stoppageToPreview, setStoppageToPreview] = useState<StoppageEvent | undefined>(undefined)
  const [stoppageIdToPreview, setStoppageIdToPreview] = useState<string | undefined>(undefined)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [isSplitMode, _setIsSplitMode] = useState<boolean>(false)
  const [showSplitStoppageDialog, setShowSplitStoppageDialog] = useState<boolean>(false)
  const [showDeleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [isAdjustMode, setIsAdjustMode] = useState<boolean>(false)
  const [dateHasBeenChanged, setDateHasBeenChanged] = useState<boolean>(true)
  const [stoppageClickData, setStoppageClickData] = useState<StoppageDataDto>()
  const [isMaxEditableBarCountExceeded, setIsMaxEditableBarCountExceeded] = useState<boolean>(false)

  const [zoomedRange, setZoomedRange] = useState<DateRange>({
    startDate: moment.tz(from, timezone),
    endDate: moment.tz(to, timezone)
  })
  useEffect(() => {
    setZoomedRange({
      startDate: moment.tz(from, timezone),
      endDate: moment.tz(to, timezone)
    })
  }, [from, to, timezone])

  const updateStoppageIdToPreview = useCallback(
    (
      dateRange: DateRange,
      equipmentRunningTimes?: EquipmentRunningTimes[],
      assetId = equipmentId
    ) => {
      setStoppageToPreview(undefined)
      const selectedEqAndRunningTimes = equipmentRunningTimes?.find(
        (eq) => eq.equipment?.id === assetId
      )
      const firstCockpitStoppageId = getFirstStoppageId(
        dateRange,
        selectedEqAndRunningTimes?.runningTimes
      )
      setStoppageIdToPreview(firstCockpitStoppageId)
    },
    [equipmentId]
  )

  const {data: plantConfig} = usePlantConfig({plantId, language})

  const onSuccesfullyGetRunningTimes = useCallback(
    (equipmentRunningTimes: EquipmentRunningTimes[]) => {
      const selectedEqAndRunningTimes = equipmentRunningTimes.find(
        (eq) => eq.equipment?.id === equipmentId
      )
      if (selectedEqAndRunningTimes?.equipment) {
        setEquipmentIdToPreview(selectedEqAndRunningTimes.equipment.id)
      }
      if (dateHasBeenChanged) {
        updateStoppageIdToPreview(zoomedRange, equipmentRunningTimes)
        setDateHasBeenChanged(false)
      }
    },
    [equipmentId, dateHasBeenChanged, zoomedRange, updateStoppageIdToPreview]
  )

  const {
    data,
    isLoading: isLoadingRunningTimes,
    refetch,
    isError,
    isRefetching
  } = usePlantRunningTimes(
    {plantId, from: from.toISOString(), to: to.toISOString()},
    {onSuccess: onSuccesfullyGetRunningTimes}
  )

  const handleDateRangeChange = (newTimeRange: string) => {
    setTimeRangeParam(newTimeRange)
    setDateHasBeenChanged(true)
  }

  const enableMainEquipmentPrefilling = useCommonFeatureFlag(FeatureFlagKey.MainEquipmentPrefill, {
    plantId
  })

  const handleClose = (stoppage?: Stoppage) => {
    setShowCreateForm(false)
    _setIsSplitMode(false)
    if (stoppage) setStoppageIdToPreview(stoppage.id)
  }

  const handleMobileClick = useCallback(
    (rt: RunningTimeDto, equipment?: EquipmentData, stoppage?: RunningTimeStoppage) => {
      setStoppageClickData({runningTime: rt, stoppage, equipment})
      setIsMobileDrawerOpen(true)
    },
    []
  )

  const onZoomReset = useCallback(
    (assetId?: string) => {
      const nextDateRange = {
        startDate: moment.tz(from, timezone),
        endDate: moment.tz(to, timezone)
      }
      setZoomedRange(nextDateRange)
      updateStoppageIdToPreview(nextDateRange, data, assetId)
    },
    [data, from, timezone, to, updateStoppageIdToPreview]
  )

  const handleSetEquipmentIdToPreview = useCallback(
    (id?: string) => {
      setStoppageToPreview(undefined)
      setStoppageIdToPreview(undefined)
      setShowCreateForm(false)
      setEquipmentIdToPreview(id)
      onZoomReset(id)
    },
    [onZoomReset]
  )

  const handleStoppageAnalysisEquipmentChange = (equipmentId: string) => {
    handleSetEquipmentIdToPreview(equipmentId)
    replaceEquipmentIdParam(equipmentId)
  }

  const handleClick = useCallback(
    async (rt: RunningTimeDto, equipment?: EquipmentData, stoppage?: RunningTimeStoppage) => {
      if (stoppage?.cockpitStoppageId) {
        setStoppageIdToPreview(stoppage.cockpitStoppageId)
        setStoppageToPreview(undefined)
        setShowCreateForm(false)
      } else {
        setStoppageToPreview(undefined)
        setStoppageIdToPreview(undefined)
        if (rt.begin && equipment) {
          const defaultValue = await getInitialStoppageData({
            rt,
            equipment,
            plantId,
            queryClient,
            axiosInstance: ApiClient.axiosInstance,
            stoppage,
            enableMainEquipmentPrefilling
          })
          setStoppageData(defaultValue)
        }
        setShowCreateForm(true)
      }
    },
    [plantId, queryClient, enableMainEquipmentPrefilling]
  )

  const onEditSuccess = useCallback((stoppage: StoppageEvent) => {
    setIsEditMode(false)
    setStoppageToPreview(stoppage)
  }, [])

  const onDeleteSuccess = useCallback(() => {
    setStoppageToPreview(undefined)
    setStoppageIdToPreview(undefined)
    setDeleteDialog(false)
  }, [])

  const setPreviewStoppage = useCallback(
    (stoppage?: StoppageEvent) => {
      setStoppageToPreview(stoppage)
      setStoppageIdToPreview(undefined)
      setShowCreateForm(false)
    },
    [setStoppageToPreview, setStoppageIdToPreview, setShowCreateForm]
  )

  const setSplitMode = (stoppage: StoppageEvent) => {
    _setIsSplitMode(true)
    setShowSplitStoppageDialog(true)
    setStoppageToPreview(stoppage)
  }

  const handleStoppageSplit = (stoppage: Partial<Stoppage>) => {
    setStoppageIdToPreview(undefined)
    setStoppageToPreview(undefined)
    setShowCreateForm(true)
    setStoppageData(stoppage)
  }

  const stoppageId = stoppageToPreview?.id || stoppageIdToPreview

  const handlePreviewClose = () => {
    setStoppageToPreview(undefined)
    setStoppageIdToPreview(undefined)
  }

  const handleDelete = (stoppage: StoppageEvent) => {
    setDeleteDialog(true)
    setStoppageToPreview(stoppage)
  }

  const handleZoomChange = (timeRange: DateRange) => {
    setZoomedRange(timeRange)
    updateStoppageIdToPreview(timeRange, data)
  }

  const disableResetZoom = useMemo(() => {
    const filtersTimeRange = {
      startDate: moment.tz(from, timezone),
      endDate: moment.tz(to, timezone)
    }
    return isAdjustMode || isSameDateRange(filtersTimeRange, zoomedRange)
  }, [isAdjustMode, zoomedRange, from, to, timezone])

  return (
    <PageContainer>
      <StoppageAnalysisHeader
        disableAdjustMode={isMaxEditableBarCountExceeded}
        isAdjustMode={isAdjustMode}
        timeRangeParam={timeRangeParam}
        onTimeRangeParamChange={handleDateRangeChange}
        timezone={timezone}
        onAdjustModeClick={() => setIsAdjustMode(true)}
        pageTitle={pageTitle}
      />
      <DataContentWrapper
        showLoader={isLoadingRunningTimes && !data}
        data={data}
        retryFunction={() => void refetch()}
        isError={isError}
        isRefetching={isRefetching}
      >
        <StoppageAnalysisLayout
          isMobile={isMobile}
          stoppageAnalysisGraphSlot={
            data && (
              <StoppageAnalysisGraph
                plantId={plantId}
                equipmentId={equipmentId}
                data={data}
                setIsMaxEditableBarCountExceeded={setIsMaxEditableBarCountExceeded}
                timeRange={zoomedRange}
                timezone={timezone}
                isAdjustMode={isAdjustMode}
                disableResetZoom={disableResetZoom}
                onDragZoom={handleZoomChange}
                onZoomReset={onZoomReset}
                equipmentIdToPreview={equipmentIdToPreview}
                onEquipmentChange={handleStoppageAnalysisEquipmentChange}
                handleStoppageClick={isMobile ? handleMobileClick : handleClick}
                onEditCancel={() => setIsAdjustMode(false)}
              />
            )
          }
          stoppageAnalysisListSlot={
            <StoppageAnalysisListContainer
              plantId={plantId}
              equipmentId={equipmentId}
              timeRange={zoomedRange}
              timezone={timezone}
              onRowClick={setPreviewStoppage}
              stoppageToPreview={stoppageToPreview}
            />
          }
          stoppageAnalysisSideCardSlot={
            stoppageId && (
              <StoppageAnalysisSideCard
                isEditMode={isEditMode}
                onEdit={setIsEditMode}
                onDelete={handleDelete}
                onSplit={setSplitMode}
                stoppageId={stoppageId}
                onEditSuccess={onEditSuccess}
                plantId={plantId}
                onClose={handlePreviewClose}
                timezone={timezone}
              />
            )
          }
          showStoppageAnalysisSideCard={!!stoppageId}
          stoppageCreateFormSlot={
            <StoppageCreate
              plantId={plantId}
              data-test-id="stoppage-analysis-graph-stoppage-create"
              doClose={handleClose}
              initialData={stoppageData}
              onSuccessSubmit={handleClose}
              skipOverlapValidation={isSplitMode}
            />
          }
          showCreateForm={showCreateForm}
        />
      </DataContentWrapper>

      {showSplitStoppageDialog && stoppageToPreview && (
        <SplitStoppageDialog
          timezone={timezone}
          open={showSplitStoppageDialog}
          onClose={(submit) => {
            setShowSplitStoppageDialog(false)
            if (!submit) _setIsSplitMode(false)
          }}
          stoppage={stoppageToPreview}
          onSplitSubmit={handleStoppageSplit}
          pxTrendAvailable={plantConfig?.pxTrendAvailable}
        />
      )}

      {!!(showDeleteDialog && stoppageToPreview) && (
        <StoppageDelete
          plantId={plantId}
          onSuccess={onDeleteSuccess}
          setDialogOpen={setDeleteDialog}
          stoppage={stoppageToPreview}
          showDialog={true}
        />
      )}
      {isMobile && (
        <OperationHoursDrawer
          isOpen={isMobileDrawerOpen}
          onClose={() => setIsMobileDrawerOpen(false)}
          stoppageData={stoppageClickData && mapStoppageData(stoppageClickData, timezone)}
          handleStoppageClick={(rt: RunningTime, equipment?: EquipmentData) =>
            handleClick(mapRunningTimeDto(rt), equipment)
          }
          labels={{
            createIncident: t('stoppageAnalysis.action.createIncident'),
            editIncident: t('stoppageAnalysis.action.editIncident')
          }}
        />
      )}
    </PageContainer>
  )
}
